import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { Helmet } from 'react-helmet-async';

import { useResize } from '../../hooks/useResize';
import { useAudio } from '../../hooks/useAudio';
import { isFreeModeAtom } from '../../atom/Atom';
import { THEMES, SE_LIST, NO_SCORE } from '../../constants/Consts';
import { getDisplayTitle } from '../../utils/TitleUtility';
import { isGradeUnlocked } from '../../utils/GradeUtility';

import { Loading } from '../../components/Loading';
import { TypingResult } from '../../api';
import { useTypingResult } from '../../hooks/api/useTypingResult';
import { Event } from '../events';
import { ChangeImageWhenHover } from '../../components/ChangeImageWhenHover';
import { SoundControlButton } from '../../components/SoundControlButton';
import { SoundControlModal } from '../../components/SoundControlModal';
import { BackToTownButton } from '../../components/BackToTownButton';
import { CreditButton } from '../../components/CreditButton';

interface IndexProps {
  themeType: THEMES;
}

const NEXT_GRADE_INDEX = 0;
const CURRENT_GRADE_INDEX = 1;
const PREV_GRADE_INDEX = 2;

export const Index = ({ themeType }: IndexProps) => {
  const { playBgmAudio, playSeAudio, stopAllAudio } = useAudio();
  const { START_BUTTON, CLICK_BUTTON } = SE_LIST;
  const isFreeMode = useAtomValue(isFreeModeAtom);

  const env = import.meta.env;
  const { resizeHandler, size, getCurrentSizeStyle } = useResize(16 / 7);
  const [selectedGrade, setSelectedGrade] = useState<number>(0);
  const { getTypingResults, typingResults } = useTypingResult();
  const navigate = useNavigate();
  const animationRefs = useRef<HTMLDivElement[]>([]);
  const nextGrade: TypingResult | undefined = useMemo(
    () => typingResults?.find((x) => x.gradeNumber === selectedGrade + 1),
    [typingResults, selectedGrade]
  );
  const prevGrade: TypingResult | undefined = useMemo(
    () => typingResults?.find((x) => x.gradeNumber === selectedGrade - 1),
    [typingResults, selectedGrade]
  );
  const currentGrade: TypingResult | undefined = useMemo(
    () => typingResults?.find((x) => x.gradeNumber === selectedGrade),
    [typingResults, selectedGrade]
  );
  useEffect(() => () => stopAllAudio(), [stopAllAudio]);
  useEffect(() => {
    playBgmAudio(`/sounds/${themeType}/practice_menu.mp3`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playBgmAudio]);

  useEffect(() => {
    window.addEventListener('resize', resizeHandler);
    resizeHandler();

    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [resizeHandler]);

  const fetchGrades = useCallback(async () => {
    await getTypingResults(themeType);
    setTimeout(() => {
      animationRefs.current.forEach((element) => {
        element?.classList.remove('animate-popup');
      });
    }, 200);
  }, [getTypingResults, themeType]);

  useEffect(() => {
    fetchGrades();
  }, [fetchGrades]);

  useEffect(() => {
    if (!typingResults || typingResults.length === 0) {
      return;
    }
    const clearedGrades = typingResults?.filter((x) => x.firstClearedAt);
    if (clearedGrades.length === 0) {
      setSelectedGrade(typingResults[0].gradeNumber);
      return;
    }
    const gradeNumber = clearedGrades.slice(-1)[0].gradeNumber;
    setSelectedGrade(gradeNumber > 1 ? gradeNumber - 1 : 1);
  }, [typingResults]);

  const playAnimation = useCallback(() => {
    animationRefs.current.forEach((element) => {
      element?.classList.add('animate-popup');
    });
    setTimeout(() => {
      animationRefs.current.forEach((element) => {
        element?.classList.remove('animate-popup');
      });
    }, 100);
  }, []);

  const handleClickMoveButton = useCallback(
    (moveGradeNumber: number) => {
      playAnimation();
      playSeAudio(CLICK_BUTTON);
      setSelectedGrade(moveGradeNumber);
    },
    [playAnimation, playSeAudio, CLICK_BUTTON]
  );

  const isGradePlayable = useCallback(
    (typingResult: TypingResult | undefined) => {
      if (!typingResult) {
        return false;
      }
      if (isFreeMode) {
        return true;
      }
      return typingResults ? isGradeUnlocked(typingResults, typingResult.gradeNumber) : false;
    },
    [typingResults, isFreeMode]
  );

  const createGradeElement = useCallback(
    (typingResult: TypingResult | undefined, index: number) => {
      if (!typingResult) {
        return <div className="w-[20%]" />;
      }

      if (isGradePlayable(typingResult)) {
        return (
          <div
            className="relative w-[20%] flex justify-center animate-popup"
            ref={(element) => {
              if (element) {
                animationRefs.current[index] = element;
              }
            }}
          >
            {typingResult.firstClearedAt && (
              <img className="absolute w-[25%] top-[10%] right-[10%]" src="/common/result_type/clear.webp" alt={'クリア'} />
            )}
            <img src={`/${themeType}/menu/${'unlock.webp'}`} alt={'コース'} />
            <div className="absolute bottom-[35%] flex flex-col items-center">
              <img className="w-[50%]" src={`${env.VITE_DIGITANE_CONTENTS_URL}/typing/grades/${typingResult?.gradeNumber}.webp`} alt={'X級'} />
              <img className="w-[50%]" src={`/${themeType}/menu/${typingResult.characterType}_icon.webp`} alt={'アイコン'} />
              <ChangeImageWhenHover
                handleClick={() => {
                  playSeAudio(START_BUTTON);
                  navigate(`/practice/${themeType}/${typingResult.gradeNumber}/`);
                }}
                frameClassName={'w-[70%]'}
                src={'/common/buttons/start_button'}
                alt={'練習スタート'}
              />
            </div>
          </div>
        );
      }

      return (
        <div
          className="relative w-[20%] flex justify-center animate-popup"
          ref={(element) => {
            if (element) {
              animationRefs.current[index] = element;
            }
          }}
        >
          <img src={`/${themeType}/menu/lock.webp`} alt={'コース'} />
          <img
            className="absolute w-[50%] top-[-10%]"
            src={`${env.VITE_DIGITANE_CONTENTS_URL}/typing/grades/${typingResult.gradeNumber}.webp`}
            alt={'X級'}
          />
          <p className={`absolute bottom-[25%] text-white font-bold ${getCurrentSizeStyle('tb:text-lg pc:text-xl pc_lg:text-2xl')}`}>
            {typingResult.gradeNumber + 1}
            <ruby>
              級<rt>きゅう</rt>
            </ruby>
            をクリアしよう
          </p>
        </div>
      );
    },
    [navigate, themeType, getCurrentSizeStyle, playSeAudio, START_BUTTON, env.VITE_DIGITANE_CONTENTS_URL, isGradePlayable]
  );

  if (!typingResults) {
    return <Loading />;
  }

  return (
    <>
      <Helmet>
        <title>{getDisplayTitle(themeType)}</title>
      </Helmet>
      <SoundControlModal />
      <SoundControlButton />
      <div className="relative w-full h-screen flex items-center justify-center">
        <img className="absolute w-full h-full top-0 object-cover" src={`/${themeType}/menu/background.webp`} alt={'背景'} />
        <img
          className="absolute w-full h-full top-0 animate-background-effect"
          src={`/${themeType}/menu/background_effect.webp`}
          alt={'背景エフェクト'}
        />
        <div style={size} className="relative w-full flex flex-col items-center justify-center select-none">
          <img className="w-[30%] mt-[4%]" src={`/${themeType}/menu/title.webp`} alt={'タイトル'} />
          <div className="relative w-full mt-8 mb-6 flex item-center justify-center">
            {createGradeElement(nextGrade, NEXT_GRADE_INDEX)}
            {nextGrade ? (
              <ChangeImageWhenHover
                handleClick={() => {
                  handleClickMoveButton(selectedGrade + 1);
                }}
                frameClassName={'w-[10%] flex items-center'}
                src={'/common/buttons/move_left_icon'}
                alt={'次の級へ'}
              />
            ) : (
              <div className="w-[10%]" />
            )}
            {createGradeElement(currentGrade, CURRENT_GRADE_INDEX)}
            {prevGrade ? (
              <ChangeImageWhenHover
                handleClick={() => {
                  handleClickMoveButton(selectedGrade - 1);
                }}
                frameClassName={'w-[10%] flex items-center'}
                src={'/common/buttons/move_right_icon'}
                alt={'前の級へ'}
              />
            ) : (
              <div className="w-[10%]" />
            )}
            {createGradeElement(prevGrade, PREV_GRADE_INDEX)}
          </div>
          <div className={`relative w-[50%] mt-[-2%] flex items-center justify-center ${isGradePlayable(currentGrade) ? '' : 'opacity-0'}`}>
            <img src="/common/menu_score_frame.webp" alt="スコア枠" />
            <div className={`absolute w-[70%] pb-[1%] flex justify-around font-bold text-sub_text ${getCurrentSizeStyle('tb:text-base pc:text-xl')}`}>
              <span>ぜんかい：{currentGrade?.playedScores[0] ?? NO_SCORE}</span>
              <span>さいこう：{currentGrade?.bestScore ?? NO_SCORE}</span>
            </div>
          </div>
        </div>
      </div>
      <BackToTownButton buttonType="hover" />
      <Event selectedThemeType={themeType} />
      {themeType === THEMES.MICRATCH && <CreditButton />}
      {themeType === THEMES.DISNEY && (
        <span className="fixed bottom-[1%] w-full flex flex-col items-center text-xxs pc:text-xs pc_lg:text-sm text-copyright">
          © Disney. © Disney/Pixar. ©Disney. Winnie the Pooh based on the "Winnie the Pooh" works by A.A. Milne and E.H. Shepard.
        </span>
      )}
    </>
  );
};
